/**
 * Small helpers for reading Auth v2 JWT claims client-side.
 *
 * Used by the eager-fetching contexts (UserContext, UserProfileContext,
 * NotificationsContext) to decide whether the stored access token is
 * "scoped to a profile" (has `userProfileId`) or a chooser-pending
 * identity-only token issued by `/api/v2/auth/login` for multi-profile
 * users with no LastActiveProfileId.
 *
 * Firing profile-scoped API calls (`/auth/me`, `/user-profile`,
 * `/notifications`, etc.) on an identity-only token returns 401, which
 * the axios 401 interceptor then interprets as "session expired" and
 * clears tokens — trapping the user on the login page. Gating those
 * calls on {@link isScopedAccessToken} avoids the race entirely.
 *
 * Source of truth for the claim shape: LoginCommandV2 /
 * SwitchProfileCommand on the backend.
 */

interface MinimalJwtClaims {
    userProfileId?: string | number;
    userType?: string;
    businessId?: string | number;
    availableProfiles?: string;
    chooserPending?: string | boolean;
}

function decodeClaims(token: string): MinimalJwtClaims | null {
    try {
        const payload = token.split('.')[1];
        if (!payload) return null;
        const padLen = (4 - (payload.length % 4)) % 4;
        const padded = payload + '='.repeat(padLen);
        const json = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(json) as MinimalJwtClaims;
    } catch {
        return null;
    }
}

/**
 * Returns true when the access token carries a `userProfileId` claim,
 * which is the invariant every profile-scoped endpoint relies on.
 * Returns false for:
 *   - missing / unreadable tokens
 *   - Auth v2 identity-only tokens issued to multi-profile users waiting
 *     on the post-login chooser (these carry `availableProfiles` but no
 *     `userProfileId`)
 */
export function isScopedAccessToken(token: string | null | undefined): boolean {
    if (!token) return false;
    const claims = decodeClaims(token);
    if (!claims) return false;
    return claims.userProfileId != null;
}

/**
 * Returns the numeric `userProfileId` claim from the access token, or null
 * when the token is identity-only / absent / unreadable.
 */
export function readUserProfileId(token: string | null | undefined): number | null {
    if (!token) return null;
    const claims = decodeClaims(token);
    if (!claims || claims.userProfileId == null) return null;
    const n = typeof claims.userProfileId === 'number'
        ? claims.userProfileId
        : parseInt(claims.userProfileId, 10);
    return Number.isNaN(n) ? null : n;
}

/**
 * Returns the numeric `businessId` claim from the access token, or null
 * when the claim is absent / zero / unreadable.
 *
 * The Onboarding wizard (PR-F, AB#1638) uses this to detect when the
 * stored JWT pre-dates the SaveDraft that created the user's first
 * Business — at which point the wizard proactively calls /auth/refresh
 * so that downstream calls (e.g. /api/subscriptions/activate-trial which
 * reads `_currentUser.BusinessId` from the JWT) see the populated claim.
 *
 * Returns null for the value 0 specifically, so callers can use a single
 * truthy check to mean "JWT carries a real businessId".
 */
export function readBusinessId(token: string | null | undefined): number | null {
    if (!token) return null;
    const claims = decodeClaims(token);
    if (!claims || claims.businessId == null) return null;
    const n = typeof claims.businessId === 'number'
        ? claims.businessId
        : parseInt(String(claims.businessId), 10);
    if (Number.isNaN(n) || n <= 0) return null;
    return n;
}
